import { useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useDashboardModals } from 'contexts/DashboardModalsContext'
import { JobsFieldSort, SortOrder } from 'generated/graphql'
import { useDashboardMutations, useDashboardsQueries } from 'hooks-queries/dashboards'
import { useTranslate } from 'lib/tolgee'
import { analyticsTracker, TOLGEE_GLOBALS } from 'utils'
import { getJobUpdatedDate } from 'utils/getJobUpdatedDate'

import {
  CREATE_DASHBOARD_FOLDER_LOG,
  LOAD_DASHBOARD_LIST_LOG,
  PAGINATION_LIMIT,
} from './create-dashboard-folder-modal.consts'
import { translations } from './create-dashboard-folder-modal.i18n'
import type { ICreateDashboardFolderModalViewBaseProps } from './create-dashboard-folder-modal.types'

export const useCreateDashboardFolderModalModel = (): ICreateDashboardFolderModalViewBaseProps => {
  const [folderName, setFolderName] = useState('')
  const [selectedDashboards, setSelectedDashboards] = useState<string[]>([])
  const t = useTranslate()

  const { createToast } = useToast()
  const { setIsCreateDashboardFolderOpen } = useDashboardModals()

  const { data, loading, fetchMore, error } = useDashboardsQueries({
    queryName: 'getListWorkspaceJobPaginated',
    queryVariables: {
      pagination: { take: PAGINATION_LIMIT },
      term: '',
      status: null,
      type: null,
      sort: {
        order: SortOrder.Desc,
        field: JobsFieldSort.UpdatedAt,
      },
      withoutFolder: true,
    },
    errorLog: LOAD_DASHBOARD_LIST_LOG,
  })

  const [createJobExternalFolder, { loading: isMutationLoading }] = useDashboardMutations({
    mutationName: 'createJobExternalFolder',
    errorLog: CREATE_DASHBOARD_FOLDER_LOG,
    options: {
      refetchQueries: ['getListWorkspaceJobPaginated', 'GET_JOB_EXTERNAL_FOLDERS'],
    },
  })

  const dashboardList = useMemo(() => {
    return (
      data?.listWorkspaceJobPaginated?.data?.map(item => {
        return {
          name: item.name,
          subtitle: getJobUpdatedDate(item, t),
          id: item.id,
          isSelected: selectedDashboards.includes(item.id),
        }
      }) || []
    )
  }, [data?.listWorkspaceJobPaginated?.data, selectedDashboards, t])

  const isSubmitDisabled = isMutationLoading || loading || !folderName

  const handleSelectDashboard = (dashboardId: string) => {
    const { DASHBOARD_FOLDERS } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(DASHBOARD_FOLDERS.CLICK_DASHBOARD_TO_ADD, {
      dashboardId,
      folderName,
    })
    setSelectedDashboards(prevState => {
      if (!prevState.length) {
        return [dashboardId]
      }
      if (prevState.includes(dashboardId)) {
        return prevState.filter(item => item !== dashboardId)
      }
      return [...prevState, dashboardId]
    })
  }

  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> &
    React.ChangeEventHandler<HTMLTextAreaElement> = event => {
    const value = event.target.value
    setFolderName(value)
  }

  const handleCloseModal = () => {
    setIsCreateDashboardFolderOpen(false)
  }

  const handleCreateDashboardFolder = () => {
    const trimmedTitle = folderName.trim()
    if (!trimmedTitle.length) {
      createToast({
        content: t(translations.invalidName),
      })
      return
    }

    const { DASHBOARD_FOLDERS } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(DASHBOARD_FOLDERS.CREATE_DASHBOARD_FOLDER, {
      folderName,
      dashboards: selectedDashboards.join(', '),
    })

    createJobExternalFolder({
      variables: {
        input: {
          name: folderName,
          workspaceJobIds: selectedDashboards,
        },
      },
    })
      .then(({ data }) => {
        if (
          data?.createWorkspaceJobExternalFolder?.__typename === 'WorkspaceJobExternalFolderModel' &&
          data?.createWorkspaceJobExternalFolder?.name
        ) {
          createToast({
            content: t(translations.success),
          })
        }
      })
      .catch(() => {
        createToast({
          content: t(TOLGEE_GLOBALS.somethingWentWrong),
        })
      })
      .finally(() => {
        handleCloseModal()
      })
  }

  const loadMore = async () => {
    const cursor = data?.listWorkspaceJobPaginated?.cursor
    if (loading || !hasMore) return
    try {
      await fetchMore({
        variables: {
          pagination: {
            take: PAGINATION_LIMIT,
            cursor,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult.listWorkspaceJobPaginated) {
            return prev
          }

          return Object.assign({}, prev, {
            listWorkspaceJobPaginated: {
              ...fetchMoreResult.listWorkspaceJobPaginated,
              data: [
                ...(prev.listWorkspaceJobPaginated?.data || []),
                ...(fetchMoreResult?.listWorkspaceJobPaginated?.data || []),
              ],
            },
          })
        },
      })
    } catch {
      // Errors are reported centrally by the Apollo ErrorLink.
    }
  }

  const hasMore = Boolean(data?.listWorkspaceJobPaginated?.cursor)

  const [sentryRef] = useInfiniteScroll({
    loading: loading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
    disabled: !!error,
  })

  return {
    dashboardList,
    handleInputChange,
    inputValue: folderName,
    handleSelectDashboard,
    handleCreateDashboardFolder,
    isSubmitDisabled,
    handleCloseModal,
    hasMore,
    sentryRef,
  }
}
